import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { AISettings, UpdateSettingFunction } from './types';
import { Calendar, CalendarDays } from 'lucide-react';

/**
 * O que faz: Seção de Horários de Funcionamento da IA
 * Onde usa: AISettingsForm
 * 
 * Permite configurar os horários específicos em que a IA
 * deve estar ativa durante dias úteis, sábados e domingos
 * 
 * REFATORADO: Cards por dia da semana com visual premium
 */

interface AIBusinessHoursSectionProps {
  settings: AISettings;
  updateSetting: UpdateSettingFunction;
  readOnly?: boolean;
}

export const AIBusinessHoursSection = ({ settings, updateSetting, readOnly = false }: AIBusinessHoursSectionProps) => {
  // Só renderiza se o modo não for 24/7
  if (settings.ai_operating_mode === '24/7') {
    return null;
  }

  return (
    <Card className="rounded-2xl border-border shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
            <Calendar className="w-5 h-5 text-accent-foreground" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold text-foreground">
              Horários de Funcionamento
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-0.5">
              Configure quando a IA deve estar ativa
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* === Dias úteis === */}
        <div className="p-4 rounded-xl bg-muted border border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-info-subtle flex items-center justify-center">
              <CalendarDays className="w-4 h-4 text-info-text" />
            </div>
            <Label className="text-base font-semibold text-foreground">
              Segunda a Sexta-feira
            </Label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="weekday-start" className="text-sm text-muted-foreground">
                Início
              </Label>
              <Input
                id="weekday-start"
                type="time"
                value={settings.ai_business_hours_start_weekday}
                onChange={(e) => !readOnly && updateSetting('ai_business_hours_start_weekday', e.target.value)}
                disabled={readOnly}
                className="rounded-xl bg-white border-border h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="weekday-end" className="text-sm text-muted-foreground">
                Fim
              </Label>
              <Input
                id="weekday-end"
                type="time"
                value={settings.ai_business_hours_end_weekday}
                onChange={(e) => !readOnly && updateSetting('ai_business_hours_end_weekday', e.target.value)}
                disabled={readOnly}
                className="rounded-xl bg-white border-border h-11"
              />
            </div>
          </div>
        </div>

        {/* === Sábado === */}
        <div className={`p-4 rounded-xl border transition-all duration-200 ${settings.ai_active_saturday
            ? 'bg-muted border-border'
            : 'bg-muted/50 border-border'
          }`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${settings.ai_active_saturday
                  ? 'bg-accent'
                  : 'bg-secondary'
                }`}>
                <span className={`text-sm font-bold ${settings.ai_active_saturday
                    ? 'text-accent-foreground'
                    : 'text-muted-foreground'
                  }`}>S</span>
              </div>
              <Label className="text-base font-semibold text-foreground">
                Sábado
              </Label>
            </div>
            <Switch
              checked={settings.ai_active_saturday}
              onCheckedChange={(checked) => !readOnly && updateSetting('ai_active_saturday', checked)}
              disabled={readOnly}
            />
          </div>
          {settings.ai_active_saturday && (
            <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-200">
              <div className="space-y-2">
                <Label htmlFor="saturday-start" className="text-sm text-muted-foreground">
                  Início
                </Label>
                <Input
                  id="saturday-start"
                  type="time"
                  value={settings.ai_saturday_hours_start}
                  onChange={(e) => !readOnly && updateSetting('ai_saturday_hours_start', e.target.value)}
                  disabled={readOnly}
                  className="rounded-xl bg-white border-border h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="saturday-end" className="text-sm text-muted-foreground">
                  Fim
                </Label>
                <Input
                  id="saturday-end"
                  type="time"
                  value={settings.ai_saturday_hours_end}
                  onChange={(e) => !readOnly && updateSetting('ai_saturday_hours_end', e.target.value)}
                  disabled={readOnly}
                  className="rounded-xl bg-white border-border h-11"
                />
              </div>
            </div>
          )}
        </div>

        {/* === Domingo === */}
        <div className={`p-4 rounded-xl border transition-all duration-200 ${settings.ai_active_sunday
            ? 'bg-muted border-warning/30'
            : 'bg-muted/50 border-border'
          }`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${settings.ai_active_sunday
                  ? 'bg-warning-subtle'
                  : 'bg-secondary'
                }`}>
                <span className={`text-sm font-bold ${settings.ai_active_sunday
                    ? 'text-warning-text'
                    : 'text-muted-foreground'
                  }`}>D</span>
              </div>
              <Label className="text-base font-semibold text-foreground">
                Domingo
              </Label>
            </div>
            <Switch
              checked={settings.ai_active_sunday}
              onCheckedChange={(checked) => !readOnly && updateSetting('ai_active_sunday', checked)}
              disabled={readOnly}
            />
          </div>
          {settings.ai_active_sunday && (
            <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-200">
              <div className="space-y-2">
                <Label htmlFor="sunday-start" className="text-sm text-muted-foreground">
                  Início
                </Label>
                <Input
                  id="sunday-start"
                  type="time"
                  value={settings.ai_sunday_hours_start}
                  onChange={(e) => !readOnly && updateSetting('ai_sunday_hours_start', e.target.value)}
                  disabled={readOnly}
                  className="rounded-xl bg-white border-border h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sunday-end" className="text-sm text-muted-foreground">
                  Fim
                </Label>
                <Input
                  id="sunday-end"
                  type="time"
                  value={settings.ai_sunday_hours_end}
                  onChange={(e) => !readOnly && updateSetting('ai_sunday_hours_end', e.target.value)}
                  disabled={readOnly}
                  className="rounded-xl bg-white border-border h-11"
                />
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
